import { createConsumer } from '@rails/actioncable'
import { authFetch, BASE_BACKEND_URL } from '../../auth'
import { exportIsOnline, newestExportSnapshot } from './snapshotMerge'

// Una suscripción autorizada por pestaña, compartida por las exportaciones pendientes.
const watchers = new Set()
let consumer
let subscription
let connecting = false
let connected = false
let reconnectTimer
let connectionTimer
let ticketController
let generation = 0
let reconnectDelay = 2000

// Cierra la conexión, cancela solicitudes y temporizadores e invalida callbacks anteriores.
function stop() {
  generation += 1
  clearTimeout(reconnectTimer)
  clearTimeout(connectionTimer)
  ticketController?.abort()
  ticketController = null
  reconnectTimer = null
  const previous = consumer
  const previousSubscription = subscription
  consumer = null
  subscription = null
  connected = false
  connecting = false
  previousSubscription?.unsubscribe()
  previous?.disconnect()
}

// Avisa a las esperas y programa otro intento con demora creciente si hay conexión y observadores.
function reconnect() {
  stop()
  if (!watchers.size) return
  watchers.forEach((watcher) => watcher.wake(null))
  if (!exportIsOnline()) return
  reconnectTimer = setTimeout(connect, reconnectDelay)
  reconnectDelay = Math.min(reconnectDelay * 2, 30000)
}

// Solicita un ticket, valida la URL del backend y abre el canal compartido de exportaciones.
async function connect() {
  if (consumer || connecting || !watchers.size || !exportIsOnline()) return
  connecting = true
  const version = generation
  ticketController = new AbortController()
  // Reinicia el intento si la conexión no se establece en diez segundos.
  connectionTimer = setTimeout(() => {
    if (version === generation) reconnect()
  }, 10000)
  try {
    const response = await authFetch(`${BASE_BACKEND_URL}/reports/user_reports/cable_ticket`, {
      method: 'POST',
      signal: ticketController.signal
    })
    if (!response?.ok) throw new Error('Realtime unavailable')
    const ticket = await response.json()
    if (version !== generation || !watchers.size) return
    const url = new URL(ticket.url)
    const backend = new URL(BASE_BACKEND_URL)
    if (
      url.protocol !== (backend.protocol === 'https:' ? 'wss:' : 'ws:') ||
      url.host !== backend.host
    )
      throw new Error('Invalid cable URL')
    url.searchParams.set('report_export_ticket', ticket.ticket)
    consumer = createConsumer(url.toString())
    subscription = consumer.subscriptions.create('ReportExportsChannel', {
      // Marca el canal como conectado, restablece la demora y despierta las esperas activas.
      connected() {
        if (version !== generation) return
        connected = true
        clearTimeout(connectionTimer)
        connecting = false
        reconnectDelay = 2000
        watchers.forEach((watcher) => watcher.wake(null))
      },
      // Intenta reconectar cuando se pierde la conexión de la generación vigente.
      disconnected() {
        if (version === generation) reconnect()
      },
      // Solicita una nueva conexión cuando el servidor rechaza la suscripción vigente.
      rejected() {
        if (version === generation) reconnect()
      },
      // Entrega cada evento a los observadores del trabajo correspondiente, descartando estados antiguos.
      received(event) {
        if (version !== generation) return
        watchers.forEach((watcher) => {
          if (watcher.jobId !== event.job_id || !event.status) return
          const next = newestExportSnapshot(watcher.latest, event)
          if (next === watcher.latest) return
          watcher.latest = next
          watcher.wake(next)
        })
      }
    })
  } catch {
    if (version === generation) reconnect()
  }
}

/**
 * Observa una exportación por su jobId y comparte el socket con las demás exportaciones.
 * La señal de cancelación retira el observador. Devuelve métodos para esperar eventos,
 * incorporar estados recibidos por HTTP, consultar la conexión y cerrar la observación.
 */
export function watchReportExport(jobId, signal) {
  let pending
  let waiter
  let closed = false
  const watcher = {
    jobId,
    // Resuelve la espera activa o guarda el evento hasta la próxima llamada a wait.
    wake(event) {
      if (closed) return
      if (waiter) waiter(event)
      else if (event !== null || pending === undefined) pending = event
    }
  }
  watchers.add(watcher)
  connect()
  // Retira este observador y cierra el socket si ya no quedan exportaciones observadas.
  const close = () => {
    if (closed) return
    closed = true
    watchers.delete(watcher)
    signal.removeEventListener('abort', close)
    waiter?.(null)
    if (!watchers.size) stop()
  }
  watcher.close = close
  signal.addEventListener('abort', close, { once: true })
  if (signal.aborted) close()
  return {
    // Expone el estado de conexión actual del canal compartido.
    get connected() {
      return connected
    },
    close,
    // Combina un estado externo con el último conocido sin retroceder el progreso.
    accept(snapshot) {
      watcher.latest = newestExportSnapshot(watcher.latest, snapshot)
      return watcher.latest
    },
    // Devuelve el evento pendiente o espera uno nuevo; devuelve null si vence el plazo o se despierta sin evento.
    wait(ms) {
      signal.throwIfAborted()
      if (pending !== undefined) {
        const event = pending
        pending = undefined
        return Promise.resolve(event)
      }
      // Mantiene una espera activa hasta recibir un evento, cerrar el observador o agotar el plazo.
      return new Promise((resolve) => {
        // Finaliza sin evento al alcanzar el tiempo máximo de espera.
        const timer = setTimeout(() => finish(null), ms)
        // Limpia el temporizador y entrega el resultado a quien espera.
        const finish = (event) => {
          clearTimeout(timer)
          waiter = null
          resolve(event)
        }
        waiter = finish
      })
    }
  }
}

if (typeof window !== 'undefined') {
  // Detiene el socket al perder conexión y libera las esperas para que puedan continuar por otra vía.
  window.addEventListener('offline', () => {
    stop()
    watchers.forEach((watcher) => watcher.wake(null))
  })
  // Reconecta al recuperar internet si todavía hay exportaciones observadas.
  window.addEventListener('online', () => {
    if (!watchers.size) return
    reconnectDelay = 2000
    connect()
    watchers.forEach((watcher) => watcher.wake(null))
  })
  // Retira todos los observadores y cierra la conexión al limpiar la sesión.
  window.addEventListener('ubiqua-session-cleared', () => {
    ;[...watchers].forEach((watcher) => watcher.close())
    stop()
  })
}
